import {
  CONFIG_CLASS_ID,
  TConfigFieldNameId,
  IConfigClassRequirements,
  IConfigValidation,
  TConfigAdditionalActionId
} from '../types/product.configuration.types';
import { PRODUCT_CATEGORY } from '../types/category';
import {
  CONFIG_EXCLUSIONS,
  CONFIG_FIELD_NAME_TO_ID,
  TConfigFieldNameToId,
  CONFIG_CLASS_NAME_TO_ID,
  REQUIRED_CONFIG_EVIDENCE,
  TConfigClassNameToId,
  CONFIG_CLASS,
  IConfigClass,
  CONFIG_CLASS_REQUIRED_ADDITIONAL_ACTIONS,
  TConfigClassRequiredAdditionalActions,
  TRANSLATED_CONFIGURATION_CLASS_NAMES,
  TRANSLATED_CONFIGURATION_FIELD_NAMES,
  TConfigFieldName
} from '../constants/configurations.constants';
import { IAdditionalActions } from '../types/orders';

/**
 * Obtiene los requisitos para una clase de configuración específica
 * @param configClassId - ID de la clase de configuración
 * @returns Requisitos de la clase de configuración
 */
export const getConfigClassRequirements = (configClassId: CONFIG_CLASS_ID): IConfigClassRequirements => {
  const requiredEvidence = REQUIRED_CONFIG_EVIDENCE.filter(item => item.configClassId === configClassId).map(
    item => item.evidenceNameId
  );

  const excludedClasses = CONFIG_EXCLUSIONS.filter(item => item.configClassId === configClassId).map(
    item => item.excludedConfigClassId
  );

  return {
    configClassId,
    requiredEvidence,
    excludedClasses
  };
};

/**
 * Valida si una configuración cumple con los requisitos
 * @param configClassId - ID de la clase de configuración
 * @param evidence - Evidencia proporcionada
 * @param existingConfigs - Configuraciones existentes para verificar conflictos
 * @returns Resultado de la validación
 */
export const validateConfig = (
  configClassId: CONFIG_CLASS_ID,
  evidence?: { evidenceNameId: number; value: string }[],
  existingConfigs?: CONFIG_CLASS_ID[]
): IConfigValidation => {
  const requirements = getConfigClassRequirements(configClassId);
  const providedEvidenceIds = evidence?.map(item => item.evidenceNameId) || [];
  const missingEvidence = requirements.requiredEvidence.filter(evidenceId => !providedEvidenceIds.includes(evidenceId));

  const conflicts: CONFIG_CLASS_ID[] = [];
  if (existingConfigs) {
    conflicts.push(
      ...existingConfigs.filter(existingConfigId => requirements.excludedClasses.includes(existingConfigId))
    );
  }

  const isValid = missingEvidence.length === 0 && conflicts.length === 0;

  return {
    isValid,
    missingEvidence,
    conflicts
  };
};

/**
 * Obtiene todas las clases de configuración disponibles para un tipo específico
 * @param configTypeId - ID del tipo de configuración
 * @returns Array de IDs de clases de configuración
 */
export const getConfigClassesByType = (configTypeId: number): CONFIG_CLASS_ID[] => {
  const configTypeToClasses: Record<number, CONFIG_CLASS_ID[]> = {
    1: [
      // BASIC
      CONFIG_CLASS_ID.DEP,
      CONFIG_CLASS_ID.APPLE_CARE_PLUS,
      CONFIG_CLASS_ID.MDM_ENROLLMENT,
      CONFIG_CLASS_ID.APPLE_CONFIGURATOR,
      CONFIG_CLASS_ID.HASH_ID,
      CONFIG_CLASS_ID.CREAR_ADMIN,
      CONFIG_CLASS_ID.ENCRIPTAR_BITLOCKER
    ],
    2: [
      // ADVANCE
      CONFIG_CLASS_ID.CONFIG_AVANZADA
    ],
    3: [
      // WIPING
      CONFIG_CLASS_ID.WIPING
    ],
    4: [
      // WARRANTY
      CONFIG_CLASS_ID.GARANTIA_1_ANO,
      CONFIG_CLASS_ID.GARANTIA_3_ANOS
    ],
    5: [
      // DIAGNOSTIC
      CONFIG_CLASS_ID.DIAGNOSTICO
    ]
  };

  return configTypeToClasses[configTypeId] || [];
};

/**
 * Verifica si dos clases de configuración son compatibles
 * @param configClassId1 - Primera clase de configuración
 * @param configClassId2 - Segunda clase de configuración
 * @returns true si son compatibles, false si hay conflicto
 */
export const areConfigClassesCompatible = (
  configClassId1: CONFIG_CLASS_ID,
  configClassId2: CONFIG_CLASS_ID
): boolean => {
  const exclusion1 = CONFIG_EXCLUSIONS.find(
    item => item.configClassId === configClassId1 && item.excludedConfigClassId === configClassId2
  );

  const exclusion2 = CONFIG_EXCLUSIONS.find(
    item => item.configClassId === configClassId2 && item.excludedConfigClassId === configClassId1
  );

  return !exclusion1 && !exclusion2;
};

/**
 * Función utilitaria para obtener la clase de configuración
 * @param configClassNameOrId - Nombre o ID de la clase de configuración
 * @returns Clase de configuración o null si no se encuentra
 */
export const getConfigClass = (configClassNameOrId: string | number | null | undefined): IConfigClass | null => {
  if (!configClassNameOrId) {
    return null;
  }
  if (typeof configClassNameOrId === 'number') {
    return CONFIG_CLASS[configClassNameOrId as CONFIG_CLASS_ID] || null;
  }
  const configClassId = CONFIG_CLASS_NAME_TO_ID[configClassNameOrId as TConfigClassNameToId];
  return CONFIG_CLASS[configClassId as CONFIG_CLASS_ID] || null;
};

/**
 * Categorías de productos que pueden tener configuraciones adicionales
 */
const CONFIGURABLE_PRODUCT_CATEGORIES = [
  PRODUCT_CATEGORY.LAPTOP,
  PRODUCT_CATEGORY.CTO_LAPTOPS,
  PRODUCT_CATEGORY.DEP_LAPTOPS
];

/**
 * Verifica si un producto es configurable (puede tener configuraciones adicionales)
 * @param category - Categoría del producto
 * @returns true si el producto es configurable, false en caso contrario
 */
export const isProductConfigurable = (category: PRODUCT_CATEGORY | string | null | undefined): boolean => {
  if (!category) return false;
  return CONFIGURABLE_PRODUCT_CATEGORIES.includes(category as PRODUCT_CATEGORY);
};

/**
 * Función utilitaria para obtener el nombre legible de una clase de configuración
 * @param classNameOrId - Nombre (clave) o id (valor) de la clase que viene de la base de datos
 * @returns Nombre legible en español o 'Sin información' si no se encuentra
 */
export const getConfigClassDisplayName = (classNameOrId: string | number | null | undefined): string => {
  const configClass = getConfigClass(classNameOrId);
  if (!configClass) {
    return 'Sin información';
  }
  return configClass.name;
};

/**
 * Función utilitaria para obtener el nombre legible de un campo de configuración.
 * @param configClassNameOrId - Nombre o ID de la clase de configuración
 * @param fieldNameOrId - Nombre o ID del campo que viene de la base de datos
 * @returns Nombre legible en español, nombre original o 'Sin información' si no se encuentra
 */
export const getConfigFieldDisplayName = (
  configClassNameOrId: string | number | null | undefined,
  fieldNameOrId: string | number | null | undefined
): string => {
  const configClass = getConfigClass(configClassNameOrId);
  if (!configClass || !fieldNameOrId) {
    return 'Sin información';
  }

  const fieldId =
    typeof fieldNameOrId === 'number'
      ? (fieldNameOrId as TConfigFieldNameId)
      : CONFIG_FIELD_NAME_TO_ID[fieldNameOrId as TConfigFieldNameToId];

  const field = configClass.fields?.find(field => field.id === fieldId);
  return field?.label || String(fieldNameOrId);
};

/**
 * Función utilitaria para obtener una acción adicional específica del array de acciones adicionales
 * @param additionalActions - Array de acciones adicionales
 * @param actionId - ID de la acción a buscar
 * @returns La acción adicional si existe, null en caso contrario
 */
export const getAdditionalAction = (
  additionalActions: IAdditionalActions[] | null | undefined,
  actionId: TConfigAdditionalActionId
): IAdditionalActions | null => {
  if (!additionalActions) return null;
  return additionalActions.find(action => action.actionId === actionId) || null;
};

/**
 * Función utilitaria para verificar si todas las acciones adicionales están confirmadas
 * @param additionalActions - Array de acciones adicionales
 * @returns true si todas las acciones están confirmadas o en caso de que el array no exista o esté vacio, false en caso contrario
 */
export const checkAllActionsConfirmed = (additionalActions: IAdditionalActions[] | null | undefined): boolean => {
  if (!additionalActions || additionalActions.length === 0) return true;
  return additionalActions.every(action => action.confirmedAt !== null);
};

/**
 * Función utilitaria para verificar si una configuración requiere una acción adicional
 * @param configClassName - Nombre de la clase de configuración
 * @returns true si la configuración requiere una acción adicional, false en caso contrario
 */
export const hasConfigRequiredAdditionalAction = (configClassName?: string | null | undefined): boolean => {
  if (!configClassName) return false;
  const configClassId = CONFIG_CLASS_NAME_TO_ID[configClassName as TConfigClassNameToId];
  if (!configClassId) return false;
  return CONFIG_CLASS_REQUIRED_ADDITIONAL_ACTIONS.includes(configClassId as TConfigClassRequiredAdditionalActions);
};

export const getI18nKeySafe = (configurationClassId?: number | null, fieldName?: string | null): string => {
  const defaultKey = 'unknownField';
  if (!configurationClassId || !fieldName) return defaultKey;

  const configClassKey = TRANSLATED_CONFIGURATION_CLASS_NAMES[configurationClassId as CONFIG_CLASS_ID];
  if (!configClassKey) return defaultKey;

  const configFieldKey = TRANSLATED_CONFIGURATION_FIELD_NAMES[fieldName as TConfigFieldName];
  if (!configFieldKey) return defaultKey;

  return `${configClassKey}.${configFieldKey}`;
};
